/**
 * GET /v1/market/status 的 symbols 缓存：
 * 交易列表点击前校验交易对是否在返回列表中。
 */
import { getMarketStatus } from '@/api/markets-api';
import type { MarketStatus, MarketStatusSymbol } from '@/types/api';

const normalizeSymbol = (symbol: string): string =>
  String(symbol || '')
    .replace(/\//g, '')
    .trim()
    .toUpperCase();

let symbolsByKey = new Map<string, MarketStatusSymbol>();
let fetchedAt = 0;
let fetchPromise: Promise<boolean> | null = null;

/** 用已拉到的 status 响应更新缓存（供 syncClock 复用同一次请求） */
export const applyMarketStatusPayload = (data: MarketStatus | null | undefined) => {
  const map = new Map<string, MarketStatusSymbol>();
  const list = Array.isArray(data?.symbols) ? data!.symbols : [];
  for (const item of list) {
    const key = normalizeSymbol(String(item?.symbol || ''));
    if (!key) continue;
    map.set(key, item);
  }
  symbolsByKey = map;
  fetchedAt = Date.now();
};

export const hasMarketStatusCache = (): boolean => fetchedAt > 0;

/** 状态接口是否包含该交易对（不含则不应进入合约页） */
export const isSymbolListedInMarketStatus = (symbol: string): boolean => {
  const key = normalizeSymbol(symbol);
  if (!key) return false;
  return symbolsByKey.has(key);
};

/**
 * 拉取 / 刷新 market/status。
 * @returns 是否成功拿到响应（失败时保留旧缓存）
 */
export const ensureMarketStatus = async (opts?: {
  maxAgeMs?: number;
  force?: boolean;
}): Promise<boolean> => {
  const maxAge = opts?.maxAgeMs ?? 30_000;
  if (!opts?.force && fetchedAt > 0 && Date.now() - fetchedAt < maxAge) {
    return true;
  }
  if (fetchPromise) return fetchPromise;

  fetchPromise = (async () => {
    try {
      const data = await getMarketStatus();
      applyMarketStatusPayload(data);
      return true;
    } catch {
      return fetchedAt > 0;
    } finally {
      fetchPromise = null;
    }
  })();

  return fetchPromise;
};
